import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { Callout, Field } from '../../../shared/components/ui/primitives';
import {
  addSiteWalletMoney,
  createSiteWallet,
  getSiteWalletHolderCandidates,
  settleSiteWallet,
  updateSiteWallet
} from '../../services/api/implementation/impl';
import { PAYMENT_MODES, SmallButton, errorMessage, inputClass, money } from './siteWalletUi';

/** Everything a wallet change can move. */
export const useRefreshWallets = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['siteWallets'] });
    queryClient.invalidateQueries({ queryKey: ['siteWallet'] });
    queryClient.invalidateQueries({ queryKey: ['siteWalletReimbursements'] });
  };
};

/**
 * The actions on one wallet. Add money: the builder side or Accounts, never
 * the holder on their own wallet. Freeze / unfreeze and settle: builder side
 * only. A closed wallet takes none of them.
 */
export const WalletActionButtons = ({ wallet, role, onSettled, size = 'sm' }) => {
  const refresh = useRefreshWallets();
  const [open, setOpen] = useState(null);
  const holder = wallet.user?.name ?? 'this wallet';

  const addMoney = useMutation({
    mutationFn: (payload) => addSiteWalletMoney(wallet.id, payload),
    onSuccess: () => {
      refresh();
      setOpen(null);
      toast.success('Money added');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not add the money'))
  });
  const setStatus = useMutation({
    mutationFn: (status) => updateSiteWallet(wallet.id, { status }),
    onSuccess: (_r, status) => {
      refresh();
      toast.success(status === 'FROZEN' ? 'Wallet frozen' : 'Wallet unfrozen');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not update the wallet'))
  });

  const mayAdd = role.isStaff && wallet.user_id !== role.userId;
  if (wallet.status === 'CLOSED' || (!mayAdd && !role.builderSide)) return null;
  const frozen = wallet.status === 'FROZEN';
  const icon = size === 'md' ? 14 : 12;

  // Clicks stop here: the buttons sit in a clickable table row, and React
  // carries clicks inside the (portalled) modals up to that row too.
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap gap-2">
        {mayAdd && (
          <SmallButton size={size} tone="primary" onClick={() => setOpen('add')}>
            <Plus size={icon} /> Add money
          </SmallButton>
        )}
        {role.builderSide && (
          <>
            <SmallButton
              size={size}
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate(frozen ? 'ACTIVE' : 'FROZEN')}
              title={
                frozen
                  ? 'Allow new expenses again'
                  : 'Stop new expenses; waiting ones can still be decided'
              }
            >
              {frozen ? <Unlock size={icon} /> : <Lock size={icon} />}{' '}
              {frozen ? 'Unfreeze' : 'Freeze'}
            </SmallButton>
            <SmallButton size={size} tone="danger" onClick={() => setOpen('settle')}>
              Settle
            </SmallButton>
          </>
        )}
      </div>
      {open === 'add' && (
        <AddMoneyModal
          holder={holder}
          busy={addMoney.isPending}
          onClose={() => setOpen(null)}
          onSubmit={(payload) => addMoney.mutate(payload)}
        />
      )}
      {open === 'settle' && (
        <SettleModal
          wallet={wallet}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            refresh();
            onSettled?.();
          }}
        />
      )}
    </div>
  );
};

/**
 * Money handed to the holder, recorded as it goes: it is in the wallet the
 * moment this is saved. There is no request or approval step.
 */
const AddMoneyModal = ({ holder, busy, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('BANK');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const n = Number(amount);
  const valid = amount !== '' && Number.isFinite(n) && n > 0;
  return (
    <Modal name={`Add money to ${holder}`} onClose={onClose} width={36}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            amount: n,
            mode,
            reference: reference.trim() || null,
            note: note.trim() || null
          });
        }}
      >
        <p className="text-sm text-gray-600">
          Record this once the money has been handed over. The balance goes up straight away.
        </p>
        <Field label="Amount (₹)" required>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
            autoFocus
          />
        </Field>
        <Field label="Paid by" required>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputClass}>
            {PAYMENT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={
            mode === 'CASH' ? 'Reference / transaction ID (optional)' : 'Reference / transaction ID'
          }
        >
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={200}
            className={inputClass}
            placeholder={
              mode === 'CASH' ? 'Voucher number' : 'UTR, UPI transaction ID or cheque number'
            }
          />
        </Field>
        <Field label="Note">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            className={inputClass}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add money'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * Settling nets what the holder holds against what they are owed, brings the
 * ledger to zero and closes the wallet. The figures here are a preview of the
 * server's settlementPlan; the server works them out again.
 */
const SettleModal = ({ wallet, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const balance = Number(wallet.balance ?? 0);
  const owed = Number(wallet.owed_to_holder ?? 0);
  // Mirrors settlementPlan on the server. Below zero they owe the company: the
  // shortfall comes out of what they're owed first, the rest they hand back.
  const negative = balance < 0;
  const held = Math.abs(balance);
  const netted = Math.min(held, owed);
  const returned = held - netted;
  const payout = owed - netted;
  const waiting = Number(wallet.on_hold ?? 0) > 0;

  const settle = useMutation({
    mutationFn: () => settleSiteWallet(wallet.id, reason.trim() ? { reason: reason.trim() } : {}),
    onSuccess: (r) => {
      toast.success(
        `Wallet settled. Returned ${money(r?.returned)}` +
          (Number(r?.payout_due) > 0 ? `; Accounts owes ${money(r.payout_due)}` : ''),
        { duration: 6000 }
      );
      onDone();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not settle the wallet'))
  });

  return (
    <Modal name={`Settle ${wallet.user?.name ?? 'wallet'}`} onClose={onClose} width={38}>
      <div className="space-y-3 text-sm">
        <p className="text-gray-600">
          This closes the wallet. Use it when the person leaves, moves out of Operations, or the
          wallet is no longer needed.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-3">
          <span className="text-gray-500">
            {negative ? 'Short (they owe the company)' : 'Cash held'}
          </span>
          <span className="text-right font-medium tabular-nums">{money(balance)}</span>
          <span className="text-gray-500">Owed to them (own pocket)</span>
          <span className="text-right font-medium tabular-nums">{money(owed)}</span>
          <span className="text-gray-500">Netted off</span>
          <span className="text-right tabular-nums">{money(netted)}</span>
          <span className="text-gray-500">They hand back</span>
          <span className="text-right font-semibold tabular-nums">{money(returned)}</span>
          <span className="text-gray-500">Accounts pays them</span>
          <span className="text-right font-semibold tabular-nums">{money(payout)}</span>
        </div>
        {waiting && (
          <Callout tone="warning">Decide the expenses still waiting before settling.</Callout>
        )}
        {negative && (
          <Callout tone="warning">
            The balance is below zero. The shortfall is taken out of what they&apos;re owed first;
            anything still short, they hand back.
          </Callout>
        )}
        <Field label="Note (optional)">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputClass}
            placeholder="e.g. Cash returned at the office"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={settle.isPending || waiting}
            onClick={() => settle.mutate()}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {settle.isPending ? 'Settling…' : 'Settle and close'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const optionalInt = (v) => (v === '' || v == null ? undefined : Number(v));

/** Opens a wallet for an Operations Manager or Executive who has none. */
export const CreateWalletModal = ({ onClose }) => {
  const refresh = useRefreshWallets();
  const [form, setForm] = useState({
    user_id: '',
    bill_required_above: '100'
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const candidates = useQuery({
    queryKey: ['siteWalletCandidates'],
    queryFn: getSiteWalletHolderCandidates
  });
  const people = candidates.data ?? [];

  const create = useMutation({
    mutationFn: createSiteWallet,
    onSuccess: (wallet) => {
      refresh();
      toast.success(`Wallet created for ${wallet?.user?.name ?? 'the site engineer'}`);
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not create the wallet'))
  });

  // The bill threshold is whole rupees on the server.
  const wholeOrBlank = (v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0);
  const valid = form.user_id && wholeOrBlank(form.bill_required_above);

  const submit = (e) => {
    e.preventDefault();
    create.mutate({
      user_id: Number(form.user_id),
      bill_required_above: optionalInt(form.bill_required_above)
    });
  };

  return (
    <Modal name="Create wallet" onClose={onClose} width={40}>
      <form className="space-y-3" onSubmit={submit}>
        <Field
          label="Site engineer"
          required
          hint={
            !candidates.isLoading && !people.length
              ? 'Every Operations Manager and Executive already has a wallet.'
              : 'Operations Managers and Executives without a wallet'
          }
        >
          <select value={form.user_id} onChange={set('user_id')} className={inputClass}>
            <option value="">{candidates.isLoading ? 'Loading…' : 'Choose a person'}</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.level === 'Medium' ? 'Manager' : 'Executive'})
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Bill needed above (₹)"
          hint="Expenses over this need a bill photo. Add money once the wallet exists."
        >
          <input
            type="number"
            min="0"
            step="1"
            value={form.bill_required_above}
            onChange={set('bill_required_above')}
            className={inputClass}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || create.isPending}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create wallet'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/** The bill threshold on an open wallet. */
export const BillRuleModal = ({ wallet, onClose }) => {
  const refresh = useRefreshWallets();
  const [form, setForm] = useState({
    bill_required_above: wallet.bill_required_above ?? 100
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const save = useMutation({
    mutationFn: (payload) => updateSiteWallet(wallet.id, payload),
    onSuccess: () => {
      refresh();
      toast.success('Wallet updated');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not update the wallet'))
  });
  const whole = (v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0);
  const valid = form.bill_required_above !== '' && whole(form.bill_required_above);

  return (
    <Modal name="Bill photo" onClose={onClose} width={36}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({
            bill_required_above: Number(form.bill_required_above)
          });
        }}
      >
        <Field label="Bill needed above (₹)" required>
          <input
            type="number"
            min="0"
            step="1"
            value={form.bill_required_above}
            onChange={set('bill_required_above')}
            className={inputClass}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || save.isPending}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
